import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

/**
 * NotificationContext — منبع واحد برای اعلان‌های کل اپلیکیشن
 *
 * استفاده از هر کامپوننتی:
 *   const { notifications, unreadCount, addNotification, markAllAsRead } = useNotifications();
 *
 *   addNotification({
 *     type: 'order',        // 'order' | 'price' | 'promotion' | 'system'
 *     title: 'سفارش جدید',
 *     message: 'سفارش شما با موفقیت ثبت شد',
 *   });
 */

const NotificationContext = createContext(null);
const MAX_NOTIFICATIONS = 50;          // حداکثر تعداد ذخیره‌شده

// ✅ کلید ذخیره‌سازی به‌ازای هر کاربر تا اعلان‌های کاربران از هم جدا بمانند
// (کاربر بدون ورود = guest). هنگام ورود/خروج صفحه reload می‌شود و کلید درست خوانده می‌شود.
const currentUserKey = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user'));
    return u && (u.phone || u.id) ? String(u.phone || u.id) : 'guest';
  } catch {
    return 'guest';
  }
};
const STORAGE_KEY = () => `noor_notifications_v1__${currentUserKey()}`;

// انواع اعلان
export const NOTIFICATION_TYPES = {
  ORDER:     'order',       // وضعیت سفارش، ارسال، تحویل
  PRICE:     'price',       // تغییر قیمت طلا
  PROMOTION: 'promotion',   // تخفیف، کد تخفیف، رویداد
  SYSTEM:    'system',      // ورود، خروج، پیام سیستمی
  GOLD_BANK: 'gold_bank',   // خرید/فروش گرم طلا
  CART:      'cart',        // اضافه به سبد، تخفیف
};

// ─── Helpers ────────────────────────────────────────────────────────
const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveToStorage = (notifications) => {
  try {
    // نگه‌داشتن فقط آخرین MAX_NOTIFICATIONS مورد
    const trimmed = notifications.slice(0, MAX_NOTIFICATIONS);
    localStorage.setItem(STORAGE_KEY(), JSON.stringify(trimmed));
  } catch (e) {
    console.warn('ذخیره اعلان در localStorage با خطا مواجه شد:', e);
  }
};

// محاسبه متن زمان نسبی (مثلاً "5 دقیقه پیش")
export const formatRelativeTime = (timestamp) => {
  const now = Date.now();
  const diff = Math.max(0, now - timestamp);
  const sec = Math.floor(diff / 1000);
  if (sec < 60)     return 'همین الان';
  const min = Math.floor(sec / 60);
  if (min < 60)     return `${min} دقیقه پیش`;
  const hr = Math.floor(min / 60);
  if (hr < 24)      return `${hr} ساعت پیش`;
  const day = Math.floor(hr / 24);
  if (day < 7)      return `${day} روز پیش`;
  const week = Math.floor(day / 7);
  if (week < 4)     return `${week} هفته پیش`;
  const month = Math.floor(day / 30);
  if (month < 12)   return `${month} ماه پیش`;
  return `${Math.floor(day / 365)} سال پیش`;
};

// ─── Provider ───────────────────────────────────────────────────────
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState(() => loadFromStorage());
  const [tick, setTick] = useState(0);   // برای refresh کردن زمان‌های نسبی

  // ─── ذخیره خودکار در localStorage ───────────────────────────────
  useEffect(() => {
    saveToStorage(notifications);
  }, [notifications]);

  // ─── به‌روزرسانی زمان‌های نسبی هر دقیقه ─────────────────────────
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // ─── محاسبه‌شده ───────────────────────────────────────────────────
  const unreadCount = notifications.filter(n => !n.read).length;

  // ─── افزودن اعلان جدید ───────────────────────────────────────────
  const addNotification = useCallback(({ type = 'system', title, message, link = null }) => {
    if (!title) return null;
    const id = Date.now() + Math.floor(Math.random() * 1000);
    const newNotif = {
      id,
      type,
      title,
      message: message || '',
      link,
      timestamp: Date.now(),
      read: false,
    };
    setNotifications(prev => [newNotif, ...prev].slice(0, MAX_NOTIFICATIONS));
    return id;
  }, []);

  // ─── علامت‌گذاری یک مورد به‌عنوان خوانده‌شده ─────────────────────
  const markAsRead = useCallback((id) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  // ─── علامت‌گذاری همه به‌عنوان خوانده‌شده ──────────────────────────
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => {
      if (prev.every(n => n.read)) return prev;   // تغییر بی‌مورد ندیم
      return prev.map(n => ({ ...n, read: true }));
    });
  }, []);

  // ─── حذف یک مورد ────────────────────────────────────────────────
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // ─── حذف همه ────────────────────────────────────────────────────
  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const value = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAll,
    _tick: tick,    // برای re-render شدن زمان‌های نسبی
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// ─── Hook اصلی ──────────────────────────────────────────────────────
export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    // در صورت استفاده خارج از Provider — fallback بی‌خطر
    return {
      notifications: [],
      unreadCount: 0,
      addNotification: () => null,
      markAsRead: () => {},
      markAllAsRead: () => {},
      removeNotification: () => {},
      clearAll: () => {},
    };
  }
  return ctx;
};

export default NotificationContext;